import type { ProgrammaticLandingPageConfig } from '../../types'
import { KWP_SIZE_FACTS } from '../kwpSizeFacts'

const FACTS = KWP_SIZE_FACTS[8]

export const KWP_8_INSTALACION_FOTOVOLTAICA: ProgrammaticLandingPageConfig = {
  slug: 'instalacion-fotovoltaica-8-kwp-precio',
  lang: 'es',
  topicId: 'kwp-kosten-8',
  title: 'Instalación fotovoltaica de 8 kWp: precio y valores orientativos',
  metaDescription:
    '¿Cuánto cuesta una instalación fotovoltaica de 8 kWp? Superficie de tejado, número de ' +
    'paneles, desglose de costes y comparación gratuita para hogares de 3-4 personas.',
  keywords: [
    'instalación fotovoltaica 8 kWp precio',
    'placas solares 8 kWp coste',
    'cuánto cuesta una instalación de 8 kWp',
    '8 kWp instalación solar hogar',
  ],
  eyebrow: 'Tamaño de instalación: 8 kWp',
  h1: 'Instalación fotovoltaica de 8 kWp: precio y valores orientativos',
  intro:
    'Una instalación de 8 kWp es un tamaño intermedio habitual para hogares de 3-4 personas con ' +
    'un consumo algo mayor o un coche eléctrico en perspectiva. Aquí encontrará valores ' +
    'orientativos sobre superficie de tejado y número de paneles, un desglose de costes y justo ' +
    'debajo una calculadora con el precio medio para 8 kWp ya calculado a partir de presupuestos ' +
    'reales de empresas instaladoras.',
  calculatorPrefill: { kind: 'kwp', kwp: 8 },
  indexPolicy: 'index',
  sections: [
    { type: 'heading', text: '¿Para quién es adecuada una instalación de 8 kWp?' },
    {
      type: 'paragraph',
      text:
        'Valor orientativo: 8 kWp suele encajar con un hogar de 3-4 personas con un consumo anual ' +
        'de unos 4.500-6.000 kWh, por ejemplo cuando hay un coche eléctrico en perspectiva pero ' +
        'todavía no hay que contar con una bomba de calor. Si se añade una bomba de calor, suele ' +
        'ser más adecuado un tamaño mayor (12 kWp en adelante).',
    },
    { type: 'heading', text: 'Tejado y paneles: lo que necesita una instalación de 8 kWp' },
    {
      type: 'paragraph',
      text:
        `Valor orientativo: unos ${FACTS.roofAreaM2} m² de tejado y aproximadamente ` +
        `${FACTS.moduleCount} paneles con módulos habituales de ~440 Wp. La producción anual, con ` +
        `orientación sur y sin sombras, ronda los ${FACTS.annualYieldKwh.toLocaleString('es-ES')} kWh. ` +
        'Un cálculo exacto para su tejado lo ofrece la calculadora de dimensionamiento.',
    },
    { type: 'heading', text: 'Componentes del coste de una instalación fotovoltaica' },
    {
      type: 'list',
      items: [
        'Paneles: el mayor coste individual, normalmente en torno a un tercio del total',
        'Inversor: con 8 kWp suele bastar todavía un único inversor de cadena (string)',
        'Montaje/mano de obra: fijación en el tejado, cableado',
        'Andamiaje: depende de la altura y accesibilidad del tejado',
        'Instalación eléctrica: adaptación del cuadro eléctrico, conexión a la red de la vivienda',
        'Tramitación: registro ante la distribuidora y organismos correspondientes',
      ],
    },
    {
      type: 'paragraph',
      text:
        'En comparación con 5 kWp, en 8 kWp la tramitación y el andamiaje básico se reparten ya ' +
        'entre más potencia instalada - el precio por kWp suele salir algo más económico.',
    },
    { type: 'heading', text: 'Amortización y autoconsumo' },
    {
      type: 'paragraph',
      text:
        'Sin batería, el autoconsumo de una instalación de 8 kWp suele rondar el 25-30 %; con una ' +
        'batería bien dimensionada y un coche eléctrico como consumidor adicional durante el día, ' +
        'suele ser bastante más alto. Un cálculo exacto para su tejado lo ofrece la calculadora de ' +
        'dimensionamiento.',
    },
  ],
  faq: [
    {
      q: '¿Para qué hogares es adecuada una instalación de 8 kWp?',
      a: 'Normalmente para hogares de 3-4 personas con un consumo algo mayor o un coche eléctrico en perspectiva, pero sin bomba de calor. Con bomba de calor, suele ser más adecuado un tamaño mayor.',
    },
    {
      q: '¿Necesito un inversor trifásico con 8 kWp?',
      a: 'No necesariamente - muchas instalaciones de 8 kWp funcionan todavía con un único inversor de cadena. La necesidad exacta depende de la acometida eléctrica de la vivienda y la tecnología elegida; la empresa instaladora lo aclara en el presupuesto.',
    },
    {
      q: '¿Cuánta superficie de tejado necesito para 8 kWp?',
      a: `Valor orientativo: unos ${FACTS.roofAreaM2} m², según el tipo de panel elegido y la orientación del tejado.`,
    },
  ],
  primaryCta: { label: 'Solicitar presupuesto gratis ahora', action: 'customer' },
  secondaryCta: {
    label: 'Coste de una instalación fotovoltaica: guía completa',
    action: 'link',
    href: '/coste-instalacion-fotovoltaica',
  },
  relatedLinks: [
    { label: 'Instalación fotovoltaica de 5 kWp: precio y valores orientativos', href: '/instalacion-fotovoltaica-5-kwp-precio' },
    { label: 'Instalación fotovoltaica de 10 kWp: precio y valores orientativos', href: '/instalacion-fotovoltaica-10-kwp-precio' },
    { label: 'Instalación fotovoltaica de 12 kWp: precio y valores orientativos', href: '/instalacion-fotovoltaica-12-kwp-precio' },
    { label: 'Cómo funciona la comparación de presupuestos', href: '/comparar-presupuestos-fotovoltaicos' },
  ],
}
